import React, { useState } from 'react';
import styles from './LoadCredits.module.css';

function fmt(n) { return `$${Number(n).toFixed(2)}`; }

const STEPS = ['Select Client', 'Select Package', 'Confirm', 'Receipt'];

export default function LoadCredits({ clients, config, refresh, showToast, API, authHeader }) {
  const [step, setStep]           = useState(0);
  const [search, setSearch]       = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPkg, setSelectedPkg]       = useState(null);
  const [customAmount, setCustomAmount]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [sendEmail, setSendEmail]   = useState(true);
  const [receipt, setReceipt]     = useState(null);

  const packages = config?.creditPackages || [];

  const filteredClients = clients.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    const athletes = c.athletes?.map(a => a.name).join(' ') || c.athlete || '';
    return c.name.toLowerCase().includes(q) || athletes.toLowerCase().includes(q);
  });

  const selectedAmount = selectedPkg
    ? packages.find(p => p.id === selectedPkg)?.amount
    : customAmount ? parseFloat(customAmount) : null;

  const reset = () => {
    setStep(0); setSearch(''); setSelectedClient(null);
    setSelectedPkg(null); setCustomAmount(''); setReceipt(null); setSendEmail(true); setLoading(false);
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setStep(1);
  };

  // Step 2 Continue → Step 3 Confirm
  const handleContinue = () => {
    if (!selectedPkg && !customAmount) { showToast('Please select a package or enter an amount', 'error'); return; }
    setStep(2);
  };

  // Step 3 Load → fires API
  const handleLoad = async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      const body = customAmount && !selectedPkg
        ? { customAmount: parseFloat(customAmount) }
        : { packageId: selectedPkg };

      const res = await fetch(`${API}/clients/${selectedClient.id}/load-credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ ...body, suppressEmail: !sendEmail })
      });

      if (!res.ok) { const e = await res.json(); showToast(e.error, 'error'); return; }
      const data = await res.json();

      setReceipt({
        clientName: selectedClient.name,
        packageLabel: data.transaction.note,
        amount: data.transaction.amount,
        previousBalance: data.transaction.balanceBefore,
        newBalance: data.transaction.balanceAfter,
        emailSent: data.emailSent,
      });
      setStep(3);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setLoading(false); }
  };

  const balStatus = (b) => b <= 0 ? 'empty' : b < 75 ? 'low' : b < 150 ? 'ok' : 'good';

  return (
    <div className={styles.page}>

      {/* ── Step indicator ───────────────────────────────────────────────── */}
      <div className={styles.stepper}>
        {STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <div className={`${styles.step} ${i === step ? styles.stepActive : ''} ${i < step ? styles.stepDone : ''}`}>
              <div className={styles.stepCircle}>
                {i < step ? '✓' : i + 1}
              </div>
              <span className={styles.stepLabel}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`${styles.stepLine} ${i < step ? styles.stepLineDone : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 0: Select Client ────────────────────────────────────────── */}
      {step === 0 && (
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Select Client</h2>
          <input
            className={styles.search}
            placeholder="Search by parent or athlete name..."
            value={search}
            autoFocus
            onChange={e => setSearch(e.target.value)}
          />
          <div className={styles.clientList}>
            {filteredClients.length === 0 && (
              <p className={styles.empty}>No clients match your search.</p>
            )}
            {filteredClients.map(c => {
              const athletes = c.athletes?.filter(a => a.name).map(a => a.name).join(', ') || c.athlete || '';
              return (
                <button key={c.id} className={styles.clientRow} onClick={() => handleSelectClient(c)}>
                  <div className={styles.avatar}>{c.name.charAt(0)}</div>
                  <div className={styles.clientInfo}>
                    <div className={styles.clientName}>{c.name}</div>
                    {athletes && <div className={styles.clientSub}>Athlete: {athletes}</div>}
                  </div>
                  <div className={`${styles.clientBal} ${styles[`bal_${balStatus(c.balance)}`]}`}>
                    {fmt(c.balance)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Step 1: Select Package ───────────────────────────────────────── */}
      {step === 1 && selectedClient && (
        <div className={styles.card}>
          <div className={styles.cardTitleRow}>
            <h2 className={styles.cardTitle}>Select Package</h2>
            <button className={styles.backBtn} onClick={() => setStep(0)}>← Change Client</button>
          </div>

          {/* Selected client summary */}
          <div className={styles.selectedClient}>
            <div className={styles.avatar}>{selectedClient.name.charAt(0)}</div>
            <div className={styles.clientInfo}>
              <div className={styles.clientName}>{selectedClient.name}</div>
              <div className={styles.clientSub}>Current balance: <strong>{fmt(selectedClient.balance)}</strong></div>
            </div>
          </div>

          {/* Credit packages */}
          <div className={styles.pkgGrid}>
            {packages.map(pkg => (
              <button key={pkg.id}
                className={`${styles.pkgCard} ${selectedPkg === pkg.id ? styles.pkgSelected : ''}`}
                onClick={() => { setSelectedPkg(pkg.id); setCustomAmount(''); }}>
                <div className={styles.pkgLabel}>{pkg.label}</div>
                <div className={styles.pkgAmount}>{fmt(pkg.amount)}</div>
                {pkg.description && <div className={styles.pkgDesc}>{pkg.description}</div>}
              </button>
            ))}
          </div>

          <div className={styles.orDivider}><span>or enter custom amount</span></div>

          <div className={styles.customRow}>
            <span className={styles.customDollar}>$</span>
            <input
              className={styles.customInput}
              type="number"
              placeholder="Custom amount (e.g. 200)"
              value={customAmount}
              onChange={e => { setCustomAmount(e.target.value); setSelectedPkg(null); }}
            />
          </div>

          <button
            className={styles.confirmBtn}
            disabled={!selectedPkg && !customAmount}
            onClick={handleContinue}
          >
            Continue →
          </button>
        </div>
      )}

      {/* ── Step 2: Confirm ─────────────────────────────────────────────── */}
      {step === 2 && selectedClient && (
        <div className={styles.card}>
          <div className={styles.cardTitleRow}>
            <h2 className={styles.cardTitle}>Confirm</h2>
            <button className={styles.backBtn} onClick={() => setStep(1)}>← Change Package</button>
          </div>

          <div className={styles.confirmSummary}>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Client</span>
              <span className={styles.confirmValue}>{selectedClient.name}</span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Package</span>
              <span className={styles.confirmValue}>
                {selectedPkg
                  ? packages.find(p => p.id === selectedPkg)?.label
                  : `Custom — ${fmt(parseFloat(customAmount))}`}
              </span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Amount</span>
              <span className={`${styles.confirmValue} ${styles.confirmAmount}`}>
                +{fmt(selectedAmount)}
              </span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Current Balance</span>
              <span className={styles.confirmValue}>{fmt(selectedClient.balance)}</span>
            </div>
            <div className={`${styles.confirmRow} ${styles.confirmRowTotal}`}>
              <span className={styles.confirmLabel}>New Balance After Load</span>
              <span className={`${styles.confirmValue} ${styles.confirmTotal}`}>
                {fmt(parseFloat(selectedClient.balance || 0) + (selectedAmount || 0))}
              </span>
            </div>
          </div>

          <label className={styles.emailToggle}>
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={e => setSendEmail(e.target.checked)}
            />
            <span>
              {sendEmail
                ? <>✉ Receipt will be emailed to {selectedClient.email}</>
                : <>📭 No receipt email will be sent</>}
            </span>
          </label>

          <button className={styles.confirmBtn} disabled={loading} onClick={handleLoad}>
            {loading ? 'Processing...' : sendEmail ? `Load ${fmt(selectedAmount)} & Send Receipt` : `Load ${fmt(selectedAmount)}`}
          </button>
        </div>
      )}

      {/* ── Step 3: Receipt ──────────────────────────────────────────────── */}
      {step === 3 && receipt && (
        <div className={styles.card}>
          <div className={styles.receiptHeader}>
            <div className={styles.receiptCheck}>✓</div>
            <h2 className={styles.receiptTitle}>Credits Loaded</h2>
            <p className={styles.receiptSub}>
              {!sendEmail
                ? 'Credits loaded — no email sent'
                : receipt.emailSent
                ? `Receipt emailed to ${selectedClient?.email}`
                : 'Credits loaded successfully'}
            </p>
          </div>

          <div className={styles.confirmSummary}>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Client</span>
              <span className={styles.confirmValue}>{receipt.clientName}</span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Package</span>
              <span className={styles.confirmValue}>{receipt.packageLabel}</span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Amount Loaded</span>
              <span className={`${styles.confirmValue} ${styles.confirmAmount}`}>+{fmt(receipt.amount)}</span>
            </div>
            <div className={styles.confirmRow}>
              <span className={styles.confirmLabel}>Previous Balance</span>
              <span className={styles.confirmValue}>{fmt(receipt.previousBalance)}</span>
            </div>
            <div className={`${styles.confirmRow} ${styles.confirmRowTotal}`}>
              <span className={styles.confirmLabel}>New Balance</span>
              <span className={`${styles.confirmValue} ${styles.confirmTotal}`}>{fmt(receipt.newBalance)}</span>
            </div>
          </div>

          <div className={styles.receiptActions}>
            <button className={styles.confirmBtn} onClick={reset}>
              Load Credits for Another Client
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
